import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, PlusCircle, ListFilter, FileText, Download, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../components/layout/PageHeader";
import DataTable from "../../components/table/Datatable";
import PaginationFooter from "../../components/table/PaginationFooter";
import DateRangeButton from "../../components/inputs/DateRangeButton";
import SelectField from "../../components/inputs/SelectField";
import { ReactComponent as Tag } from "../../assets/images/Tag.svg";
import { ReactComponent as Home } from "../../assets/images/Home.svg";
import BatteryPill from "../../components/battery/battery";
import { createHttpClient } from "../../api/client";
import config from "../../config/env";

const TAB_REFETCH_DEBOUNCE_MS = 180;

/**
 * ✅ หน้านี้แก้ให้:
 * - ลบ mock ทั้งหมด
 * - แก้โหลดข้อมูลให้ถูกตาม tab:
 *   buy_in + sell_out -> GET /products (ส่ง tab=buy_in|sell_out)
 *   sell_accessory    -> GET /product-accessories (ใช้ product_accessory_transactions)
 *   pawn              -> GET /pawns
 *   repair            -> GET /repairs
 *   redeem            -> GET /products (ส่ง tab=redeem) (ถ้า backend แยก tab ได้)
 *
 * - ✅ แสดงจำนวนบน tab ให้ถูกต้อง:
 *   prefetch total ของทุก tab ก่อน (ยิง pageSize=1 แล้วอ่าน meta.pagination.total)
 * - ✅ กด tab แล้ว "โหลดข้อมูลก่อน" แล้วค่อยเปลี่ยน tab (lazy-load per tab + cache)
 * - ✅ ไม่โหลดซ้ำซ้อน (เหลือ fetch ตัวเดียวต่อ tab)
 *
 * หมายเหตุ: สำหรับ /products ต้องให้ backend รองรับ query param `tab`
 * เช่น /products?tab=buy_in
 */

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function joinImgUrl(base, path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const b = String(base || "").replace(/\/+$/, "");
  const p = String(path || "").replace(/^\/+/, "");
  return b && p ? `${b}/${p}` : "";
}

function formatThaiDateFromISODateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";

  const y = d.getFullYear() + 543;
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${day}/${m}/${y} ${hh}:${mi}:${ss}`;
}

function toISODateOnly(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildCreatedAtDateRangeFilters(range) {
  const [start, end] = Array.isArray(range) ? range : [null, null];
  if (!start || !end) return {};

  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return {};

  s.setHours(0, 0, 0, 0);
  e.setHours(23, 59, 59, 999);

  return {
    "filters[createdAt][$gte]": s.toISOString(),
    "filters[createdAt][$lte]": e.toISOString(),
  };
}

function isWithinSelectedDateRange(isoLike, range) {
  const [start, end] = Array.isArray(range) ? range : [null, null];
  if (!start || !end) return true;

  const d = new Date(isoLike);
  if (Number.isNaN(d.getTime())) return false;

  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return true;

  s.setHours(0, 0, 0, 0);
  e.setHours(23, 59, 59, 999);
  return d >= s && d <= e;
}

function applyFrontendSearchRows(rows, query, pickFields) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return rows;

  return (rows || []).filter((row) => {
    const fields = typeof pickFields === "function" ? pickFields(row) : [];
    const haystack = fields
      .map((x) => String(x ?? "").toLowerCase())
      .join(" ");
    return haystack.includes(q);
  });
}

function readJsonStorage(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function resolveCurrentRoleName() {
  const directRole = String(
    localStorage.getItem("userRoleName") ||
      localStorage.getItem("roleName") ||
      localStorage.getItem("auth_role_name") ||
      ""
  )
    .trim()
    .toLowerCase();
  if (directRole) return directRole;

  const profileLike =
    readJsonStorage("profile") ||
    readJsonStorage("user") ||
    readJsonStorage("auth_user") ||
    readJsonStorage("authUser") ||
    readJsonStorage("me") ||
    null;

  const nestedRole = String(
    profileLike?.user?.role?.name || profileLike?.role?.name || profileLike?.user?.roleName || ""
  )
    .trim()
    .toLowerCase();

  return nestedRole;
}

// ---------- compare helpers ----------
function toText(x) {
  return String(x ?? "").toLowerCase();
}
function compareText(a, b) {
  const A = toText(a);
  const B = toText(b);
  if (A < B) return -1;
  if (A > B) return 1;
  return 0;
}
function compareNumber(a, b) {
  const A = Number(a ?? 0);
  const B = Number(b ?? 0);
  return A - B;
}

function ManageButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-xl border-2 hover:border-none border-slate-200 bg-white px-4 py-2 text-sm font-medium text-[#047DED] active:scale-[0.98] hover:bg-[#F5F5F5]"
    >
      จัดการ
    </button>
  );
}

// ---------- Simple PDF Modal ----------
function PdfModal({ open, title = "บิล", url, onClose }) {
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 z-[200] bg-black/50" onClick={onClose} />
      <div className="fixed inset-0 z-[201] flex items-center justify-center p-4">
        <div className="w-full max-w-6xl rounded-md bg-white shadow-xl overflow-hidden">
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-3">
            <div className="flex items-center gap-2 min-w-0">
              <FileText size={18} className="text-slate-700" />
              <div className="font-semibold text-slate-900 truncate">{title}</div>
            </div>

            <div className="flex items-center gap-2">
              <a
                href={url}
                download
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-10 items-center gap-2 rounded-md border border-[#00000026] bg-white px-4 text-sm font-semibold text-slate-700 hover:shadow-sm active:scale-[0.98]"
                title="ดาวน์โหลด"
              >
                <Download size={16} />
                ดาวน์โหลด
              </a>

              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-[#00000026] bg-white text-slate-700 hover:shadow-sm active:scale-[0.98]"
                title="ปิด"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="h-[80vh] bg-slate-50">
            {url ? (
              <iframe title="pdf-preview" src={url} className="h-full w-full" />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-slate-500">
                ไม่พบไฟล์ PDF
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

const PRODUCT_STATUS_META = {
  available: { label: "พร้อมขาย", tone: "green" },
  unavailable: { label: "ไม่พร้อมขาย", tone: "gray" },
  sold: { label: "ขายแล้ว", tone: "blue" },
  pawned: { label: "จำนำ", tone: "amber" },
  redeemed: { label: "ไถ่ถอน", tone: "indigo" },
  in_progress: { label: "กำลังซ่อม", tone: "orange" },
  completed: { label: "ซ่อมเสร็จ", tone: "emerald" },
  draft: { label: "แบบร่าง", tone: "slate" },
};

function getStatusMeta(status) {
  const s = String(status || "").toLowerCase();
  return PRODUCT_STATUS_META[s] || { label: s || "-", tone: "gray" };
}

const TAB_ALLOWED_STATUS = {
  buy_in: [],
  sell_out: ["sold"],
  pawn: ["pawned", "reserved"],
  redeem: ["redeemed"],
  repair: ["in_progress", "completed"],
  // sell_accessory: ไม่ใช้ product_status
};

const HIDDEN_STATUS = new Set(["available", "unavailable"]);

function normalizeStatus(s) {
  return String(s || "").trim().toLowerCase();
}

function filterRowsByTabStatus(rows, tabKey) {
  // ✅ sell_accessory แสดงทุก status ไม่ต้องกรอง
  if (tabKey === "sell_accessory") return rows || [];

  const allowed = TAB_ALLOWED_STATUS[tabKey] || [];
  const allowedSet = new Set(allowed.map(normalizeStatus));

  return (rows || [])
    .filter((r) => (tabKey === "buy_in" ? true : !HIDDEN_STATUS.has(normalizeStatus(r.product_status))))
    .filter((r) => (allowed.length ? allowedSet.has(normalizeStatus(r.product_status)) : true));
}

// ---------- Tabs ----------
const TABS = [
  { key: "buy_in", label: "ซื้อเข้า" },
  { key: "sell_out", label: "ขายออก" },
  { key: "sell_accessory", label: "ขายอุปกรณ์เสริม" },
  { key: "pawn", label: "จำนำ" },
  { key: "redeem", label: "ไถ่ถอน" },
  { key: "repair", label: "ซ่อม" },
];

// ---------- Status pill ----------
function StatusPill({ status }) {
  const { label, tone } = getStatusMeta(status);

  const toneCls = {
    green: "bg-emerald-50 text-emerald-700",
    gray: "bg-slate-100 text-slate-600",
    blue: "bg-blue-50 text-blue-700",
    orange: "bg-orange-50 text-orange-600",
    teal: "bg-teal-50 text-teal-700",
    purple: "bg-purple-50 text-purple-700",
    slate: "bg-slate-100 text-slate-700",
    amber: "bg-amber-50 text-amber-700",
  }[tone] || "bg-slate-100 text-slate-600";

  return (
    <span className={classNames("inline-flex rounded-xl px-3 py-1 text-sm font-semibold", toneCls)}>
      {label}
    </span>
  );
}
// function StatusPill({ text, tone = "orange" }) {
//   const cls =
//     tone === "orange" ? "bg-orange-50 text-orange-600" : "bg-slate-100 text-slate-600";
//   return (
//     <span className={classNames("inline-flex rounded-xl px-3 py-1 text-sm font-semibold", cls)}>
//       {text}
//     </span>
//   );
// }

// ---------- map responses ----------
function mapPawnRow(it) {
  const rawPawnStatus = normalizeStatus(it?.pawn_status || it?.status || "active");
  const product_status =
    rawPawnStatus === "redeemed" ? "redeemed" : rawPawnStatus === "expired" ? "unavailable" : "pawned";
  const billNo = asText(it?.bill_number || it?.bill_no || it?.documentId || it?.id);
  const pdfPath = asText(it?.report_pdf_path || it?.pdf_path || it?.pdfUrl || it?.pdf_url);
  const pdfUrl = pdfPath ? joinImgUrl(config.apiImg, pdfPath) : "";
  const productName = asText(it?.product_name).trim();
  const categoryName = asText(it?.category?.name);
  const modelName = asText(it?.model?.name || it?.model);
  const itemName =
    productName ||
    asText(it?.item_name) ||
    (categoryName && modelName ? `${categoryName} ${modelName}` : categoryName || modelName) ||
    "-";

  return {
    id: it?.id,
    created_at: it?.createdAt || it?.buy_in_date || "",
    ticket_no: it?.id || String(it?.id ?? ""),

    item: itemName,
    cap: asText(it?.capacity) || "-",
    color: asText(it?.color) || "-",
    battery: Number(it?.battery_health ?? 0),
    gear: asText(it?.equipment) || "-",
    buy_price: Number(it?.amount ?? 0),
    phone: it?.customer_phone || "-",
    warranty: asText(it?.warranty_status) || "-",

    product_status, // ✅ สำคัญ
    pawn_status: rawPawnStatus,
    imei: it?.imei || "",
    bill_no: billNo || "-",
    pdfUrl,
    due_date: it?.due_date || "",
    customer_name: it?.customer_name || "",
  };
}

function mapRepairRow(it) {
  const firstImg = Array.isArray(it?.images) ? it.images[0] : null;
  const imgUrl = joinImgUrl(
    config.apiImg,
    firstImg?.formats?.thumbnail?.url || firstImg?.url || ""
  );
  const pdfPath = asText(it?.report_pdf_path || it?.pdf_path || it?.pdfUrl || it?.pdf_url);
  const pdfUrl = pdfPath ? joinImgUrl(config.apiImg, pdfPath) : "";

  // map สถานะซ่อมให้เป็น product_status:
  // - in_progress (กำลังซ่อม) / completed (ซ่อมเสร็จ)
  const raw = normalizeStatus(it?.product_status || it?.status || it?.repair_status);
  const product_status =
    raw === "done" || raw === "completed" ? "completed" : "in_progress";

  const productName = asText(it?.product_name).trim();
  const categoryName = asText(it?.category?.name);
  const modelName = asText(it?.model?.name || it?.model);
  const itemName =
    productName ||
    asText(it?.device_name || it?.item_name) ||
    (categoryName && modelName ? `${categoryName} ${modelName}` : categoryName || modelName) ||
    "-";

  return {
    id: it?.id,
    created_at: it?.createdAt || "",
    job_no: it?.documentId || String(it?.id ?? ""),

    item: itemName,
    cap: asText(it?.capacity) || "-",
    color: asText(it?.color) || "-",
    battery: Number(it?.battery_health ?? 0),
    gear: asText(it?.equipment) || "-",

    buy_price: Number(it?.cost_estimate ?? 0),
    quality: it?.symptoms || "-",
    warranty: asText(it?.warranty_status) || "-",

    product_status, // ✅ สำคัญ
    final_cost: Number(it?.final_cost ?? 0),
    imei: it?.imei || "",
    imageUrl: imgUrl,
    pdfUrl,
  };
}

function asText(v) {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);

  // array -> join
  if (Array.isArray(v)) return v.map(asText).filter(Boolean).join(", ");

  // object -> ดึง name/label/title ก่อน
  if (typeof v === "object") {
    return (
      v.name ??
      v.label ??
      v.title ??
      v.model_name ??
      v.capacity_name ??
      v.color_name ??
      v.code ??
      v.value ??
      (v.id != null ? String(v.id) : "")
    );
  }

  return String(v);
}

/**
 * สำหรับ /products: ถ้ายังไม่รู้ response จริง
 * ให้ normalize เบื้องต้นแบบ “รองรับหลาย field name”
 * แล้วค่อยปรับ mapping ให้ตรง backend
 */
function mapProductRow(it, tabKey) {
  const created =
    it?.createdAt || it?.created_at || it?.buy_in_date || it?.sell_out_date || it?.date || "";

  const imei = asText(it?.imei || it?.IMEI || it?.serial);
  const modelObj = it?.model || {};
  const categoryObj = modelObj?.category || it?.category || {};
  const productName = asText(it?.product_name).trim();
  const categoryName = asText(categoryObj?.name);
  const modelName = asText(modelObj?.name || it?.model);
  const itemName =
    productName || (categoryName && modelName ? `${categoryName} ${modelName}` : categoryName || modelName || "");

  const cap = asText(it?.capacity?.size || it?.capacity?.name || it?.capacity || it?.cap || it?.storage);
  const color = asText(it?.color);
  const equipment = asText(it?.equipment || it?.gear);
  const warranty = asText(it?.warranty_status || it?.warranty);
  const quality = asText(it?.quality_master?.name || it?.quality_master || it?.quality || "-");
  const pdfPath = asText(it?.report_pdf_path || it?.pdf_path || it?.pdfUrl || it?.pdf_url);
  const resolvedPdfUrl = pdfPath ? joinImgUrl(config.apiImg, pdfPath) : "";

  const orderItem = itemName || asText(it?.orderItem || it?.item_name || it?.name || it?.model || it?.product_name);
  const item = itemName || asText(it?.item || it?.item_name || it?.name || it?.model || it?.product_name);

  const buyPrice = Number(it?.buy_price ?? it?.buyPrice ?? it?.amount ?? 0);
  const sellPrice = Number(it?.sell_price ?? it?.sellPrice ?? 0);

  const product_status = normalizeStatus(
    it?.product_status || it?.status || it?.productStatus || ""
  );

  return {
    id: it?.id,
    created_at: created,
    date_in: tabKey === "buy_in" ? formatThaiDateFromISODateTime(created) : "",
    date_out: tabKey === "sell_out" ? formatThaiDateFromISODateTime(created) : "",

    orderItem,
    item,

    cap,
    capNum: Number(String(cap).replace(/[^\d]/g, "") || 0),
    color,
    quality,
    gear: equipment,
    warranty,
    imei,

    buy_price: buyPrice,
    sell_price: sellPrice,

    product_status, // ✅ เพิ่มบรรทัดนี้
    category_id: String(categoryObj?.id || it?.category?.id || ""),
    model_id: String(modelObj?.id || ""),
    model_name: modelName,

    bill_no: asText(it?.bill_no || it?.billNo),
    accessory: asText(it?.accessory || it?.accessory_name),
    pdfUrl: resolvedPdfUrl,
  };
}

function mapProductTransactionRows(it) {
  const accessoryName = asText(it?.name).trim() || "-";
  const accessoryCategoryName = asText(it?.accessory_category?.name).trim();
  const brandName = asText(it?.brand?.name || it?.brand).trim();
  const orderItem =
    [accessoryCategoryName, brandName].filter(Boolean).join(" ") || accessoryCategoryName || accessoryName;

  const txList = Array.isArray(it?.product_accessory_transactions)
    ? it.product_accessory_transactions
    : [];

  const sellTransactions = txList.filter(
    (tx) => String(tx?.type || "").trim().toLowerCase() === "sell_out"
  );

  return sellTransactions.map((tx) => {
    const created = tx?.date || tx?.createdAt || tx?.created_at || it?.createdAt || "";
    const billNo = asText(tx?.bill_number || tx?.bill_no || tx?.documentId || tx?.id);
    const pdfPath = asText(tx?.pdf_path || tx?.pdfUrl || tx?.pdf_url);
    const pdfUrl = pdfPath ? joinImgUrl(config.apiImg, pdfPath) : "";

    return {
      id: `${it?.id || it?.documentId || "acc"}_${tx?.id || tx?.documentId || "tx"}`,
      created_at: created,
      bill_no: billNo,
      orderItem,
      item: orderItem,
      accessory: accessoryName,
      imei: "",
      pdfUrl,
      qty: Math.max(1, Math.abs(Number(tx?.qty_change ?? 1) || 1)),
      buy_price: Number(it?.price ?? 0),
      sell_price: Number(
        tx?.price_cash ?? tx?.price_transfer ?? tx?.price_card ?? tx?.price_others ?? it?.price ?? 0
      ),
      product_status: "sold",
    };
  });
}

function groupSellAccessoryRowsByBill(rows) {
  const grouped = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const billNo = String(row?.bill_no || "").trim();
    const key = billNo || String(row?.id || "");
    if (!key) return;

    const accessoryName = asText(row?.accessory).trim();
    const current = grouped.get(key);

    if (!current) {
      grouped.set(key, {
        ...row,
        lines: [
          {
            orderItem: row?.orderItem || row?.item || "-",
            accessory: accessoryName || "-",
          },
        ],
      });
      return;
    }

    current.lines.push({
      orderItem: row?.orderItem || row?.item || "-",
      accessory: accessoryName || "-",
    });
    if (!current.orderItem && (row?.orderItem || row?.item)) {
      current.orderItem = row?.orderItem || row?.item;
    }
    if (!current.bill_no && row.bill_no) current.bill_no = row.bill_no;
    if (!current.imei && row.imei) current.imei = row.imei;
    if (!current.pdfUrl && row.pdfUrl) current.pdfUrl = row.pdfUrl;
  });

  return Array.from(grouped.values());
}

export default function ProductsMainPage() {
  const navigate = useNavigate();
  const http = useMemo(() => createHttpClient(), []);
  const currentRoleName = resolveCurrentRoleName();
  const isAdmin = currentRoleName === "admin";
  const minSelectableDate = useMemo(() => {
    if (!isAdmin) return undefined;
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - 3);
    return d;
  }, [isAdmin]);

  // ✅ api map ตามที่คุณสั่ง
  const TAB_API = useMemo(
    () => ({
      buy_in: "/products",
      sell_out: "/products",
      sell_accessory: "/product-accessories",
      redeem: "/pawns",
      pawn: "/pawns",
      repair: "/repairs",
    }),
    []
  );

  // ---------- UI state ----------
  const [tab, setTab] = useState("buy_in");

  const [query, setQuery] = useState("");
  const [range, setRange] = useState([null, null]);

  // masterdata options
  const [categoriesOptions, setCategoriesOptions] = useState([]); // /categories (buy_in,sell_out)
  const [modelsOptions, setModelsOptions] = useState([]); // /models (filter by category)
  const [accessoriesOptions, setAccessoriesOptions] = useState([]); // /accessories (sell_accessory)
  const [mdLoading, setMdLoading] = useState(false);

  // filters
  const [categoryId, setCategoryId] = useState("");
  const [model, setModel] = useState("");
  const [sellStatus, setSellStatus] = useState(""); // เผื่อใช้ต่อ
  const [accessoryId, setAccessoryId] = useState("");
  const [pawnStatus, setPawnStatus] = useState("");
  const [redeemStatus, setRedeemStatus] = useState("");
  const [repairStatus, setRepairStatus] = useState("");

  // sort
  const [sortKey, setSortKey] = useState("");
  const [sortDir, setSortDir] = useState("asc");

  // pagination (ต่อ tab ใช้เดียวกัน)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // pdf modal
  const [openPdf, setOpenPdf] = useState(false);
  const [pdfTitle, setPdfTitle] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");

  // ---------- cache per tab ----------
  const [tabCounts, setTabCounts] = useState(() =>
    Object.fromEntries(TABS.map((t) => [t.key, 0]))
  );

  const [tabData, setTabData] = useState(() =>
    Object.fromEntries(
      TABS.map((t) => [
        t.key,
        { rows: [], total: 0, loaded: false, loading: false, error: "" },
      ])
    )
  );

  // keep per-tab pagination if you want (optional)
  const tabPageRef = useRef(Object.fromEntries(TABS.map((t) => [t.key, 1])));
  const tabFetchStateRef = useRef(
    Object.fromEntries(TABS.map((t) => [t.key, { inflight: "", done: "" }]))
  );

  const buildTabFetchKey = (tabKey, targetPage, targetPageSize) => {
    const [start, end] = Array.isArray(range) ? range : [null, null];

    return JSON.stringify({
      tabKey,
      page: Number(targetPage || 1),
      pageSize: Number(targetPageSize || 50),
      query: String(query || "").trim(),
      rangeStart: start ? new Date(start).toISOString() : "",
      rangeEnd: end ? new Date(end).toISOString() : "",
      categoryId: String(categoryId || ""),
      model: String(model || ""),
      accessoryId: String(accessoryId || ""),
      sellStatus: String(sellStatus || ""),
      pawnStatus: String(pawnStatus || ""),
      redeemStatus: String(redeemStatus || ""),
      repairStatus: String(repairStatus || ""),
      sortKey: String(sortKey || ""),
      sortDir: String(sortDir || ""),
    });
  };

  function patchTabData(tabKey, patch) {
    setTabData((prev) => ({
      ...prev,
      [tabKey]: { ...prev[tabKey], ...patch },
    }));
  }

  const onSort = (key) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return key;
    });
    // sort เปลี่ยน -> รีเซ็ต page ของ tab ปัจจุบัน
    tabPageRef.current[tab] = 1;
    setPage(1);

    // reset loaded เพื่อให้โหลดใหม่ตาม sort
    patchTabData(tab, { loaded: false });
  };

  // ---------- masterdata load: /categories or /accessories ----------
  useEffect(() => {
    let alive = true;
    const ac = new AbortController();

    (async () => {
      setMdLoading(true);
      try {
        // ✅ buy_in / sell_out ใช้ /categories
        if (tab === "buy_in" || tab === "sell_out") {
          if (categoriesOptions.length) {
            if (alive) setMdLoading(false);
            return;
          }
          const res = await http.get("/categories", { signal: ac.signal });
          const list = res?.data?.data ?? res?.data ?? [];
          const mapped = (Array.isArray(list) ? list : []).map((it) => ({
            value: it?.id ?? it?.documentId ?? it?.code ?? it?.value ?? it?.name,
            label: it?.name ?? it?.label ?? it?.title ?? String(it?.id ?? ""),
          }));
          if (!alive) return;
          setCategoriesOptions(mapped);
        } else if (tab === "sell_accessory") {
          if (accessoriesOptions.length) {
            if (alive) setMdLoading(false);
            return;
          }
          const res = await http.get("/accessories", { signal: ac.signal });
          const list = res?.data?.data ?? res?.data ?? [];
          const mapped = (Array.isArray(list) ? list : []).map((it) => ({
            value: it?.id ?? it?.documentId ?? it?.code ?? it?.value ?? it?.name,
            label: it?.name ?? it?.label ?? it?.title ?? String(it?.id ?? ""),
          }));
          if (!alive) return;
          setAccessoriesOptions(mapped);
        }
      } catch (e) {
        if (e?.name === "CanceledError" || e?.name === "AbortError") return;
        console.error("GET masterdata error:", e);
        if (!alive) return;
        setCategoriesOptions([]);
        setAccessoriesOptions([]);
      } finally {
        if (alive) setMdLoading(false);
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [http, tab, categoriesOptions.length, accessoriesOptions.length]);

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();

    (async () => {
      if (!(tab === "buy_in" || tab === "sell_out") || !categoryId) {
        setModelsOptions([]);
        return;
      }

      try {
        const res = await http.get("/models", {
          params: {
            filters: { category: { id: { $eq: Number(categoryId) || categoryId } } },
            sort: ["sequence:asc", "id:asc"],
            pagination: { pageSize: 500 },
          },
          signal: ac.signal,
        });

        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        const mapped = list
          .map((it) => ({
            value: String(it?.id ?? it?.documentId ?? ""),
            label: asText(it?.name || it?.label || it?.title || ""),
          }))
          .filter((it) => it.value && it.label);

        if (!alive) return;
        setModelsOptions(mapped);
      } catch (e) {
        if (e?.name === "CanceledError" || e?.name === "AbortError") return;
        console.error("GET /models error:", e);
        if (!alive) return;
        setModelsOptions([]);
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [http, tab, categoryId]);

  const categoryOptionsForSelect = useMemo(
    () => [{ value: "", label: "ประเภทสินค้า" }, ...categoriesOptions],
    [categoriesOptions]
  );
  const accessoryOptionsForSelect = useMemo(
    () => [{ value: "", label: "รุ่นสินค้า" }, ...accessoriesOptions],
    [accessoriesOptions]
  );

  const modelOptions = useMemo(
    () => [{ value: "", label: "รุ่นสินค้า" }, ...modelsOptions],
    [modelsOptions]
  );

  const pawnStatusOptions = useMemo(
    () => [
      { value: "active", label: "จำนำ" },
    ],
    []
  );

  const redeemStatusOptions = useMemo(
    () => [
      { value: "redeem", label: "ไถ่ถอน" },
    ],
    []
  );

  const repairStatusOptions = useMemo(
    () => [
      { value: "in_progress", label: "กำลังซ่อม" },
      { value: "completed", label: "ซ่อมเสร็จ" },
    ],
    []
  );

  // ---------- fetch total per tab (prefetch counts ก่อน) ----------
  async function fetchTabTotal(tabKey, signal) {
    const endpoint = TAB_API[tabKey] || "/products";

    const baseParams = { "pagination[page]": 1, "pagination[pageSize]": 1 };
    const createdAtFilters = buildCreatedAtDateRangeFilters(range);

    // pawn/redeem/repair นับจาก endpoint ของมัน
    if (tabKey === "pawn" || tabKey === "redeem" || tabKey === "repair") {
      const params =
        tabKey === "pawn"
          ? { ...baseParams, "filters[pawn_status][$eq]": "active", ...createdAtFilters }
          : tabKey === "redeem"
            ? { ...baseParams, "filters[pawn_status][$eq]": "redeemed", ...createdAtFilters }
          : { ...baseParams, ...createdAtFilters };
      const res = await http.get(endpoint, { params, signal });
      return Number(res?.data?.meta?.pagination?.total ?? res?.data?.total ?? 0);
    }

    if (tabKey === "sell_accessory") {
      const params = {
        "pagination[page]": 1,
        "pagination[pageSize]": 1000,
      };
      const res = await http.get(endpoint, {
        params,
        signal,
      });
      const list = res?.data?.data ?? res?.data?.rows ?? res?.data?.items ?? [];
      const mapped = (Array.isArray(list) ? list : [])
        .flatMap(mapProductTransactionRows)
        .filter((row) => isWithinSelectedDateRange(row?.created_at, range));
      const grouped = groupSellAccessoryRowsByBill(mapped);
      return Number(grouped.length || 0);
    }

    // ✅ products tabs ที่ต้อง filter ตาม product_status
    const allowed = TAB_ALLOWED_STATUS[tabKey] || [];
    const res = await http.get(endpoint, {
      params: {
        ...baseParams,
        ...(allowed.length ? { product_status: allowed.join(",") } : {}), // ถ้า backend รองรับ comma-separated
        ...createdAtFilters,
      },
      signal,
    });

    return Number(res?.data?.meta?.pagination?.total ?? res?.data?.total ?? 0);
  }

  // ---------- fetch rows per tab (lazy-load + cache) ----------
  async function fetchTabRows(tabKey, { page, pageSize }, signal) {
    const endpoint = TAB_API[tabKey] || "/products";

    const createdAtFilters = buildCreatedAtDateRangeFilters(range);

    // ---------------- pawn/redeem/repair ----------------
    if (tabKey === "pawn") {
      const params = {
        "pagination[page]": page,
        "pagination[pageSize]": pageSize,
        "filters[pawn_status][$eq]": pawnStatus || "active",
        ...createdAtFilters,
      };
      const res = await http.get(endpoint, { params, signal });
      const list = res?.data?.data ?? [];
      const total = res?.data?.meta?.pagination?.total ?? list.length ?? 0;

      let mapped = (Array.isArray(list) ? list : []).map(mapPawnRow);
      mapped = mapped
        .filter((row) => normalizeStatus(row?.pawn_status) === normalizeStatus(pawnStatus || "active"))
        .filter((row) => isWithinSelectedDateRange(row?.created_at, range));
      mapped = applyFrontendSearchRows(mapped, query, (row) => [
        row?.orderItem,
        row?.name,
        row?.phone,
        row?.imei,
        row?.warranty,
      ]);

      return { rows: mapped, total: Number(total || 0) };
    }

    if (tabKey === "redeem") {
      const params = {
        "pagination[page]": page,
        "pagination[pageSize]": pageSize,
        "filters[pawn_status][$eq]": redeemStatus || "redeemed",
        ...createdAtFilters,
      };
      const res = await http.get(endpoint, { params, signal });
      const list = res?.data?.data ?? [];
      const total = res?.data?.meta?.pagination?.total ?? list.length ?? 0;

      let mapped = (Array.isArray(list) ? list : []).map(mapPawnRow);
      mapped = mapped
        .filter((row) => normalizeStatus(row?.pawn_status) === normalizeStatus(redeemStatus || "redeemed"))
        .filter((row) => isWithinSelectedDateRange(row?.created_at, range));
      mapped = applyFrontendSearchRows(mapped, query, (row) => [
        row?.orderItem,
        row?.name,
        row?.phone,
        row?.imei,
        row?.warranty,
      ]);

      return { rows: mapped, total: Number(total || 0) };
    }

    if (tabKey === "repair") {
      const params = {
        "pagination[page]": page,
        "pagination[pageSize]": pageSize,
        ...createdAtFilters,
      };

      const res = await http.get(endpoint, { params, signal });
      const list = res?.data?.data ?? [];
      const total = res?.data?.meta?.pagination?.total ?? list.length ?? 0;

      let mapped = (Array.isArray(list) ? list : []).map(mapRepairRow);
      mapped = filterRowsByTabStatus(mapped, "repair"); // ✅ in_progress/completed เท่านั้น
      if (repairStatus) {
        mapped = mapped.filter((row) => normalizeStatus(row?.product_status) === normalizeStatus(repairStatus));
      }
      mapped = mapped.filter((row) => isWithinSelectedDateRange(row?.created_at, range));
      mapped = applyFrontendSearchRows(mapped, query, (row) => [
        row?.item,
        row?.cap,
        row?.color,
        row?.quality,
        row?.gear,
        row?.warranty,
        row?.imei,
      ]);

      return { rows: mapped, total: Number(total || 0) };
    }

    // ---------------- sell_accessory ----------------
    if (tabKey === "sell_accessory") {
      const params = {
        "pagination[page]": 1,
        "pagination[pageSize]": 1000,
      };

      const res = await http.get(endpoint, { params, signal });
      const list = res?.data?.data ?? res?.data?.rows ?? res?.data?.items ?? [];
      let mapped = (Array.isArray(list) ? list : []).flatMap(mapProductTransactionRows);
      mapped = mapped.filter((row) => isWithinSelectedDateRange(row?.created_at, range));
      mapped = applyFrontendSearchRows(mapped, query, (row) => [
        row?.bill_no,
        row?.orderItem,
        row?.item,
        row?.accessory,
      ]);
      mapped = groupSellAccessoryRowsByBill(mapped);
      const total = mapped.length;
      const safePage = Math.max(1, Number(page || 1));
      const safePageSize = Math.max(1, Number(pageSize || 1));
      const start = (safePage - 1) * safePageSize;
      const pagedRows = mapped.slice(start, start + safePageSize);
      return { rows: pagedRows, total: Number(total || 0) };
    }

    // ---------------- products: buy_in / sell_out / redeem ----------------
    const allowed = TAB_ALLOWED_STATUS[tabKey] || [];

    const params = {
      // ✅ ส่งให้ backend กรองให้ตรงแท็บ
      ...(allowed.length ? { product_status: allowed.join(",") } : {}),

      ...createdAtFilters,

      category_id: tabKey === "buy_in" || tabKey === "sell_out" ? categoryId : "",
      model: tabKey === "buy_in" || tabKey === "sell_out" ? model : "",

      sort_key: sortKey || "",
      sort_dir: sortDir || "asc",
      "pagination[page]": page,
      "pagination[pageSize]": pageSize,
    };

    const res = await http.get(endpoint, { params, signal });
    const list = res?.data?.data ?? res?.data?.rows ?? res?.data?.items ?? [];
    const total =
      res?.data?.meta?.pagination?.total ??
      res?.data?.total ??
      res?.data?.totalItems ??
      list.length ??
      0;

    let mapped = (Array.isArray(list) ? list : []).map((it) => mapProductRow(it, tabKey));

    // ✅ กันหลุดอีกชั้น (สำคัญมาก)
    mapped = filterRowsByTabStatus(mapped, tabKey);

    if (categoryId && (tabKey === "buy_in" || tabKey === "sell_out")) {
      mapped = mapped.filter((row) => String(row?.category_id || "") === String(categoryId));
    }
    if (model && (tabKey === "buy_in" || tabKey === "sell_out")) {
      mapped = mapped.filter(
        (row) => String(row?.model_id || "") === String(model) || toText(row?.model_name) === toText(model)
      );
    }
    if (sellStatus) {
      mapped = mapped.filter((row) => normalizeStatus(row?.product_status) === normalizeStatus(sellStatus));
    }
    mapped = mapped.filter((row) => isWithinSelectedDateRange(row?.created_at, range));
    mapped = applyFrontendSearchRows(mapped, query, (row) => [
      row?.orderItem,
      row?.item,
      row?.cap,
      row?.color,
      row?.quality,
      row?.gear,
      row?.warranty,
      row?.imei,
      row?.buy_price,
      row?.sell_price,
      row?.bill_no,
    ]);

    return { rows: mapped, total: Number(total || 0) };
  }

  // ✅ โหลด tab แรก (buy_in) หลัง counts มาแล้ว หรือจะโหลดทันทีเลยก็ได้
  useEffect(() => {
    let alive = true;
    const ac = new AbortController();

    (async () => {
      try {
        const countEntries = await Promise.all(
          TABS.map(async (tabItem) => {
            const total = await fetchTabTotal(tabItem.key, ac.signal);
            return [tabItem.key, Number(total || 0)];
          })
        );

        if (!alive) return;

        setTabCounts(Object.fromEntries(countEntries));
      } catch (e) {
        if (e?.name === "CanceledError" || e?.name === "AbortError") return;
        console.error("fetch tab counts error:", e);
        if (alive) {
          setTabCounts(Object.fromEntries(TABS.map((t) => [t.key, 0])));
        }
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [http, range?.[0], range?.[1]]);

  // ✅ ถ้า filter/search/range เปลี่ยน ให้ invalidate tab ปัจจุบัน แล้วโหลดใหม่ “ตอนอยู่ tab เดิม”
  useEffect(() => {
    const requestKey = buildTabFetchKey(tab, 1, pageSize);
    const currentFetchState = tabFetchStateRef.current[tab] || { inflight: "", done: "" };

    if (currentFetchState.done === requestKey || currentFetchState.inflight === requestKey) {
      tabPageRef.current[tab] = 1;
      setPage(1);
      return;
    }

    tabFetchStateRef.current[tab] = { ...currentFetchState, inflight: requestKey };

    // invalidate current tab
    patchTabData(tab, { loaded: false });
    tabPageRef.current[tab] = 1;
    setPage(1);

    let alive = true;
    const ac = new AbortController();
    const timer = setTimeout(() => {
      (async () => {
        patchTabData(tab, { loading: true, error: "" });
        try {
          const { rows, total } = await fetchTabRows(tab, { page: 1, pageSize }, ac.signal);
          if (!alive) return;

          const stateAfterSuccess = tabFetchStateRef.current[tab] || { inflight: "", done: "" };
          tabFetchStateRef.current[tab] = {
            ...stateAfterSuccess,
            inflight: "",
            done: requestKey,
          };

          patchTabData(tab, { rows, total, loaded: true, loading: false, error: "" });
        } catch (e) {
          if (e?.name === "CanceledError" || e?.name === "AbortError") return;

          const stateAfterError = tabFetchStateRef.current[tab] || { inflight: "", done: "" };
          tabFetchStateRef.current[tab] = {
            ...stateAfterError,
            inflight: stateAfterError.inflight === requestKey ? "" : stateAfterError.inflight,
          };

          const statusCode = Number(e?.response?.status || 0);
          if (statusCode === 429) {
            if (!alive) return;
            patchTabData(tab, { loading: false, error: "" });
            return;
          }

          console.error("refetch current tab error:", e);
          if (!alive) return;
          patchTabData(tab, { rows: [], total: 0, loaded: false, loading: false, error: "โหลดข้อมูลไม่สำเร็จ" });
        }
      })();
    }, TAB_REFETCH_DEBOUNCE_MS);

    return () => {
      alive = false;
      clearTimeout(timer);

      const stateOnCleanup = tabFetchStateRef.current[tab] || { inflight: "", done: "" };
      if (stateOnCleanup.inflight === requestKey) {
        tabFetchStateRef.current[tab] = { ...stateOnCleanup, inflight: "" };
      }

      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // เฉพาะตัวที่ใช้ในการ query
    tab,
    query,
    range?.[0],
    range?.[1],
    categoryId,
    model,
    accessoryId,
    sellStatus,
    pawnStatus,
    redeemStatus,
    repairStatus,
    sortKey,
    sortDir,
    pageSize,
  ]);

  // ✅ เปลี่ยนหน้า pagination ของ tab ปัจจุบัน
  useEffect(() => {
    let alive = true;
    const ac = new AbortController();
    let activeRequestKey = "";

    (async () => {
      // ถ้า page เป็น 1 แล้ว เราโหลดใน effect ข้างบนไปแล้ว
      if (page === 1) return;

      const requestKey = buildTabFetchKey(tab, page, pageSize);
      activeRequestKey = requestKey;
      const currentFetchState = tabFetchStateRef.current[tab] || { inflight: "", done: "" };
      if (currentFetchState.done === requestKey || currentFetchState.inflight === requestKey) return;

      tabFetchStateRef.current[tab] = { ...currentFetchState, inflight: requestKey };

      patchTabData(tab, { loading: true, error: "" });
      try {
        const { rows, total } = await fetchTabRows(tab, { page, pageSize }, ac.signal);
        if (!alive) return;

        const stateAfterSuccess = tabFetchStateRef.current[tab] || { inflight: "", done: "" };
        tabFetchStateRef.current[tab] = {
          ...stateAfterSuccess,
          inflight: "",
          done: requestKey,
        };

        patchTabData(tab, { rows, total, loaded: true, loading: false, error: "" });
      } catch (e) {
        if (e?.name === "CanceledError" || e?.name === "AbortError") return;

        const stateAfterError = tabFetchStateRef.current[tab] || { inflight: "", done: "" };
        tabFetchStateRef.current[tab] = {
          ...stateAfterError,
          inflight: stateAfterError.inflight === requestKey ? "" : stateAfterError.inflight,
        };

        const statusCode = Number(e?.response?.status || 0);
        if (statusCode === 429) {
          if (!alive) return;
          patchTabData(tab, { loading: false, error: "" });
          return;
        }

        console.error("pagination fetch error:", e);
        if (!alive) return;
        patchTabData(tab, { loading: false, error: "โหลดข้อมูลไม่สำเร็จ" });
      }
    })();

    return () => {
      alive = false;

      if (activeRequestKey) {
        const stateOnCleanup = tabFetchStateRef.current[tab] || { inflight: "", done: "" };
        if (stateOnCleanup.inflight === activeRequestKey) {
          tabFetchStateRef.current[tab] = { ...stateOnCleanup, inflight: "" };
        }
      }

      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, tab]);

  // ---------- active tab state ----------
  const active = tabData[tab] || { rows: [], total: 0, loading: false, error: "" };
  const totalItems = Number(active.total || 0);
  const tableLoading = !!active.loading;
  const tableError = active.error || "";

  // server pagination: rows ควรเป็นของหน้านั้นอยู่แล้ว
  const dataForTable = active.rows;

  // ---------- client sort fallback ----------
  const sortedClient = useMemo(() => {
    if (!sortKey) return dataForTable;
    const next = [...dataForTable].sort((a, b) => {
      let c = 0;
      if (["capNum", "buy_price", "sell_price", "battery"].includes(sortKey)) {
        c = compareNumber(a?.[sortKey], b?.[sortKey]);
      } else {
        c = compareText(a?.[sortKey], b?.[sortKey]);
      }
      return sortDir === "asc" ? c : -c;
    });
    return next;
  }, [dataForTable, sortKey, sortDir]);

  const paged = sortedClient;

  const headerTitle = (label, key) => (
    <span className={classNames("inline-flex min-w-0 items-center gap-2", sortKey === key && "text-[#047DED]")}>
      <span className="block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{label}</span>
    </span>
  );

  // ---------- columns ----------
  const repairColumns = useMemo(() => {
    return [
      {
        key: "item",
        title: headerTitle("รายการ", "item"),
        sortable: true,
        sortKey: "item",
        headerClassName: "w-[250px]",
        className: "w-[220px] font-semibold",
        render: (r) => <div className="truncate">{r.item || "-"}</div>,
      },
      {
        key: "cap",
        title: headerTitle("ความจุ", "cap"),
        sortable: false,
        headerClassName: "w-[90px]",
        className: "w-[90px] whitespace-nowrap",
        render: (r) => r.cap || "-",
      },
      {
        key: "color",
        title: headerTitle("สี", "color"),
        sortable: false,
        headerClassName: "w-[80px]",
        className: "w-[80px] whitespace-nowrap",
        render: (r) => r.color || "-",
      },
      {
        key: "battery",
        title: headerTitle("แบตเตอรี่", "battery"),
        sortable: false,
        headerClassName: "w-[130px]",
        className: "w-[130px] whitespace-nowrap",
        render: (r) => <BatteryPill value={r.battery ?? 0} />,
      },
      {
        key: "gear",
        title: headerTitle("อุปกรณ์", "gear"),
        sortable: false,
        headerClassName: "w-[140px]",
        className: "w-[140px]",
        render: (r) => <div className="truncate">{r.gear || "-"}</div>,
      },
      {
        key: "buy_price",
        title: headerTitle("ราคาประเมิน", "buy_price"),
        sortable: true,
        sortKey: "buy_price",
        headerClassName: "w-[130px]",
        className: "w-[130px] font-medium whitespace-nowrap",
        render: (r) => `฿${Number(r.buy_price || 0).toLocaleString("th-TH")}`,
      },
      {
        key: "quality",
        title: headerTitle("คุณภาพสินค้า", "quality"),
        sortable: false,
        headerClassName: "w-[200px]",
        className: "w-[200px] text-slate-600",
        render: (r) => <div className="truncate">{r.quality || "-"}</div>,
      },
      {
        key: "warranty",
        title: headerTitle("ประกันศูนย์", "warranty"),
        sortable: false,
        headerClassName: "w-[120px]",
        className: "w-[120px] whitespace-nowrap",
        render: (r) => r.warranty || "-",
      },
      {
        key: "bill",
        title: "บิล",
        headerClassName: "w-[120px]",
        className: "w-[120px] whitespace-nowrap",
        render: (r) =>
          r.pdfUrl ? (
            <a
              href={r.pdfUrl}
              download
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-blue-50 px-4 py-2 text-sm font-semibold text-[#047DED] active:scale-[0.98] hover:bg-[#F5F5F5]"
            >
              <FileText size={16} />
              บิล
            </a>
          ) : (
            <span className="text-transparent select-none">-</span>
          ),
      },
    ];
  }, [sortKey, navigate]);

  const pawnLikeColumns = useMemo(() => {
    const firstKey = tab === "repair" ? "job_no" : "ticket_no";
    const firstLabel = tab === "repair" ? "เลขงาน" : "เลขฝาก";

    const statusTone = tab === "pawn" ? "orange" : "gray";
    const statusText = tab === "pawn" ? "จำนำ" : tab === "redeem" ? "ไถ่ถอน" : "ซ่อม";

    return [
      {
        key: firstKey,
        title: headerTitle(firstLabel, firstKey),
        sortable: true,
        sortKey: firstKey,
        headerClassName: "w-[160px]",
        className: "w-[160px] font-medium whitespace-nowrap",
        render: (r) => <div className="truncate">{r[firstKey] || "-"}</div>,
      },
      {
        key: "item",
        title: headerTitle("รายการ", "item"),
        sortable: true,
        sortKey: "item",
        headerClassName: "w-[220px]",
        className: "w-[220px] font-medium",
        render: (r) => <div className="truncate">{r.item || "-"}</div>,
      },
      { key: "cap", title: headerTitle("ความจุ", "cap"), sortable: false, headerClassName: "w-[90px]", className: "w-[90px] whitespace-nowrap" },
      { key: "color", title: headerTitle("สี", "color"), sortable: false, headerClassName: "w-[80px]", className: "w-[80px] whitespace-nowrap" },
      {
        key: "battery",
        title: headerTitle("แบตเตอรี่", "battery"),
        sortable: false,
        headerClassName: "w-[110px]",
        className: "w-[110px] whitespace-nowrap",
        render: (r) => <BatteryPill value={r.battery ?? 0} />,
      },
      {
        key: "gear",
        title: headerTitle("อุปกรณ์", "gear"),
        sortable: false,
        headerClassName: "w-[120px]",
        className: "w-[120px]",
        render: (r) => <div className="truncate">{r.gear || "-"}</div>,
      },
      {
        key: "buy_price",
        title: headerTitle("ราคารับ", "buy_price"),
        sortable: true,
        sortKey: "buy_price",
        headerClassName: "w-[120px]",
        className: "w-[120px] font-medium whitespace-nowrap",
        render: (r) => `฿${Number(r.buy_price || 0).toLocaleString("th-TH")}`,
      },
      { key: "phone", title: headerTitle("เบอร์โทรศัพท์", "phone"), sortable: false, headerClassName: "w-[140px]", className: "w-[140px] whitespace-nowrap" },
      { key: "warranty", title: headerTitle("ประกันศูนย์", "warranty"), sortable: false, headerClassName: "w-[180px]", className: "w-[180px] whitespace-nowrap", render: (r) => <div className="truncate">{r.warranty || "-"}</div> },
      {
        key: "status",
        title: headerTitle("สถานะ", "status"),
        sortable: false,
        headerClassName: "w-[120px]",
        className: "w-[120px] whitespace-nowrap",
        render: (r) => <StatusPill status={r.product_status || r.pawn_status || r.repair_status} />
      },
      {
        key: "bill_no",
        title: headerTitle("เลขบิล", "bill_no"),
        sortable: true,
        sortKey: "bill_no",
        headerClassName: "w-[130px]",
        className: "w-[130px] whitespace-nowrap",
        render: (r) => <div className="truncate">{r.bill_no || "-"}</div>,
      },
      {
        key: "bill",
        title: "บิล",
        sortable: false,
        headerClassName: "w-[120px]",
        className: "w-[120px] whitespace-nowrap",
        render: (r) =>
          r.pdfUrl ? (
            <button
              type="button"
              onClick={() => {
                setPdfTitle(`บิล${tab === "redeem" ? "ไถ่ถอน" : "จำนำ"} #${r.bill_no || r.id || "-"}`);
                setPdfUrl(r.pdfUrl);
                setOpenPdf(true);
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-50 px-4 py-2 text-sm font-semibold text-[#047DED] active:scale-[0.98] hover:bg-[#F5F5F5]"
            >
              <FileText size={16} />
              บิล
            </button>
          ) : (
            <span className="text-transparent select-none">-</span>
          ),
      },
    ];
  }, [tab, sortKey]);

  const columns = useMemo(() => {
    if (tab === "buy_in") {
      return [
        {
          key: "date_in",
          title: headerTitle("วันที่ซื้อเข้า", "date_in"),
          sortable: true,
          sortKey: "created_at",
          headerClassName: "w-[170px]",
          className: "w-[170px] whitespace-nowrap",
          render: (r) => formatThaiDateFromISODateTime(r.created_at),
        },
        {
          key: "orderItem",
          title: headerTitle("รายการออเดอร์", "orderItem"),
          sortable: true,
          sortKey: "orderItem",
          headerClassName: "w-[260px]",
          className: "w-[260px] font-medium",
          render: (r) => <div className="truncate">{r.orderItem || r.item || "-"}</div>,
        },
        {
          key: "cap",
          title: headerTitle("ความจุ", "capNum"),
          sortable: true,
          sortKey: "capNum",
          headerClassName: "w-[90px]",
          className: "w-[90px] whitespace-nowrap",
          render: (r) => r.cap || "-",
        },
        {
          key: "color",
          title: headerTitle("สี", "color"),
          sortable: false,
          headerClassName: "w-[80px]",
          className: "w-[80px] whitespace-nowrap",
        },
        {
          key: "warranty",
          title: headerTitle("ประกันศูนย์", "warranty"),
          sortable: true,
          sortKey: "warranty",
          headerClassName: "w-[160px]",
          className: "w-[160px] whitespace-nowrap",
          render: (r) => <div className="truncate">{r.warranty || "-"}</div>,
        },
        {
          key: "imei",
          title: headerTitle("IMEI", "imei"),
          sortable: false,
          headerClassName: "w-[170px]",
          className: "w-[170px]",
          render: (r) => <div className="truncate">{r.imei || "-"}</div>,
        },
        {
          key: "buy_price",
          title: headerTitle("ราคาทุน", "buy_price"),
          sortable: true,
          sortKey: "buy_price",
          headerClassName: "w-[120px]",
          className: "w-[120px] font-medium whitespace-nowrap",
          render: (r) => `฿${Number(r.buy_price || 0).toLocaleString("th-TH")}`,
        },
        {
          key: "manage",
          title: "จัดการ",
          headerClassName: "w-[120px]",
          className: "w-[120px] whitespace-nowrap",
          render: (r) => <ManageButton onClick={() => navigate(`/app/products/stock-buy-in/${r.id}`)} />,
        },
      ];
    }

    if (tab === "sell_out") {
      return [
        {
          key: "date_out",
          title: headerTitle("วันที่ขายออก", "date_out"),
          sortable: true,
          sortKey: "created_at",
          headerClassName: "w-[170px]",
          className: "w-[170px] whitespace-nowrap",
          render: (r) => formatThaiDateFromISODateTime(r.created_at),
        },
        {
          key: "item",
          title: headerTitle("รายการ", "item"),
          sortable: true,
          sortKey: "item",
          headerClassName: "w-[220px]",
          className: "w-[220px] font-medium",
          render: (r) => <div className="truncate">{r.item || r.orderItem || "-"}</div>,
        },
        {
          key: "cap",
          title: headerTitle("ความจุ", "capNum"),
          sortable: true,
          sortKey: "capNum",
          headerClassName: "w-[90px]",
          className: "w-[90px] whitespace-nowrap",
          render: (r) => r.cap || "-",
        },
        {
          key: "color",
          title: headerTitle("สี", "color"),
          sortable: true,
          sortKey: "color",
          headerClassName: "w-[80px]",
          className: "w-[80px] whitespace-nowrap",
        },
        {
          key: "gear",
          title: headerTitle("อุปกรณ์", "gear"),
          sortable: true,
          sortKey: "gear",
          headerClassName: "w-[120px]",
          className: "w-[120px]",
          render: (r) => <div className="truncate">{r.gear || "-"}</div>,
        },
        {
          key: "warranty",
          title: headerTitle("ประกันศูนย์", "warranty"),
          sortable: true,
          sortKey: "warranty",
          headerClassName: "w-[160px]",
          className: "w-[160px] whitespace-nowrap",
          render: (r) => <div className="truncate">{r.warranty || "-"}</div>,
        },
        {
          key: "imei",
          title: headerTitle("IMEI", "imei"),
          sortable: true,
          sortKey: "imei",
          headerClassName: "w-[170px]",
          className: "w-[170px]",
          render: (r) => <div className="truncate">{r.imei || "-"}</div>,
        },
        {
          key: "sell_price",
          title: headerTitle("ราคา", "sell_price"),
          sortable: true,
          sortKey: "sell_price",
          headerClassName: "w-[120px]",
          className: "w-[120px] font-medium whitespace-nowrap",
          render: (r) => `฿${Number(r.sell_price || 0).toLocaleString("th-TH")}`,
        },
        {
          key: "manage",
          title: "บิล",
          headerClassName: "w-[120px]",
          className: "w-[120px] whitespace-nowrap",
          render: (r) =>
            r.pdfUrl ? (
              <button
                type="button"
                onClick={() => {
                  setPdfTitle(`บิลขายออก #${r.id}`);
                  setPdfUrl(r.pdfUrl);
                  setOpenPdf(true);
                }}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-50 px-4 py-2 text-sm font-semibold text-[#047DED] active:scale-[0.98] hover:bg-[#F5F5F5]"
              >
                <FileText size={16} />
                บิล
              </button>
            ) : (
              <span className="text-transparent select-none">-</span>
            ),
        },
      ];
    }

    if (tab === "sell_accessory") {
      return [
        {
          key: "bill_no",
          title: headerTitle("หมายเลขบิล", "bill_no"),
          sortable: true,
          sortKey: "bill_no",
          headerClassName: "w-[140px]",
          className: "w-[140px] whitespace-nowrap",
          render: (r) => {
            const lines = Array.isArray(r?.lines) && r.lines.length ? r.lines : [{}];

            return (
              <div className="divide-y divide-slate-200">
                {lines.map((_, index) => (
                  <div
                    key={`${r?.id || "row"}_bill_${index}`}
                    className="py-2 text-slate-700 font-medium min-h-[40px] flex items-center"
                  >
                    {index === 0 ? <span className="truncate w-full">{r.bill_no || "-"}</span> : <span>&nbsp;</span>}
                  </div>
                ))}
              </div>
            );
          },
        },
        {
          key: "orderItem",
          title: headerTitle("รายการออเดอร์", "orderItem"),
          sortable: false,
          headerClassName: "w-[260px]",
          className: "w-[260px]",
          render: (r) => {
            const lines = Array.isArray(r?.lines) && r.lines.length
              ? r.lines
              : [{ orderItem: r?.orderItem || r?.item || "-" }];

            return (
              <div className="divide-y divide-slate-200">
                {lines.map((line, index) => (
                  <div
                    key={`${r?.id || "row"}_order_${index}`}
                    className="py-2 font-semibold text-slate-800 truncate min-h-[40px] flex items-center"
                  >
                    {line?.orderItem || r?.orderItem || r?.item || "-"}
                  </div>
                ))}
              </div>
            );
          },
        },
        {
          key: "accessory",
          title: headerTitle("อุปกรณ์เสริม", "accessory"),
          sortable: false,
          headerClassName: "w-[520px]",
          className: "w-[520px]",
          render: (r) => {
            const lines = Array.isArray(r?.lines) && r.lines.length
              ? r.lines
              : [{ accessory: r?.accessory || "-" }];

            return (
              <div className="divide-y divide-slate-200">
                {lines.map((line, index) => (
                  <div
                    key={`${r?.id || "row"}_acc_${index}`}
                    className="py-2 text-slate-600 truncate min-h-[40px] flex items-center"
                  >
                    {line?.accessory || "-"}
                  </div>
                ))}
              </div>
            );
          },
        },
        {
          key: "imei",
          title: headerTitle("IMEI", "imei"),
          sortable: false,
          headerClassName: "w-[170px]",
          className: "w-[170px] whitespace-nowrap",
          render: (r) => {
            const lines = Array.isArray(r?.lines) && r.lines.length ? r.lines : [{}];

            return (
              <div className="divide-y divide-slate-200">
                {lines.map((_, index) => (
                  <div
                    key={`${r?.id || "row"}_imei_${index}`}
                    className="py-2 font-semibold text-slate-800 min-h-[40px] flex items-center"
                  >
                    {index === 0 ? (r.imei || "-") : <span>&nbsp;</span>}
                  </div>
                ))}
              </div>
            );
          },
        },
        {
          key: "bill",
          title: headerTitle("บิล", "bill"),
          sortable: false,
          headerClassName: "w-[120px]",
          pl: "pl-8",
          className: "w-[120px] whitespace-nowrap",
          render: (r) => {
            const lines = Array.isArray(r?.lines) && r.lines.length ? r.lines : [{}];

            return (
              <div className="divide-y divide-slate-200">
                {lines.map((_, index) => (
                  <div
                    key={`${r?.id || "row"}_pdf_${index}`}
                    className="py-2 min-h-[40px] flex items-center"
                  >
                    {index === 0 ? (
                      r.pdfUrl ? (
                        <a
                          href={r.pdfUrl}
                          download
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 rounded-xl bg-blue-50 px-4 py-2 text-sm font-semibold text-[#047DED] active:scale-[0.98] hover:bg-[#F5F5F5]"
                        >
                          <FileText size={16} />
                          บิล
                        </a>
                      ) : (
                        <span className="text-transparent select-none">-</span>
                      )
                    ) : (
                      <span>&nbsp;</span>
                    )}
                  </div>
                ))}
              </div>
            );
          },
        },
      ];
    }

    if (tab === "pawn") return pawnLikeColumns;
    if (tab === "redeem") return pawnLikeColumns;
    if (tab === "repair") return repairColumns;
    return pawnLikeColumns;
  }, [tab, sortKey, navigate, pawnLikeColumns, repairColumns]);

  // ---------- filter fields by tab ----------
  const filterFields = useMemo(() => {
    if (tab === "buy_in" || tab === "sell_out") {
      return (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <SelectField
            icon={ListFilter}
            label="ประเภทสินค้า"
            placeholder={mdLoading ? "กำลังโหลด..." : "ประเภทสินค้า"}
            options={categoryOptionsForSelect}
            value={categoryId}
            onChange={(v) => {
              setCategoryId(v);
              setModel("");
              tabPageRef.current[tab] = 1;
              setPage(1);
            }}
          />
          <SelectField
            icon={ListFilter}
            label="รุ่นสินค้า"
            placeholder="รุ่นสินค้า"
            options={modelOptions}
            value={model}
            onChange={(v) => {
              setModel(v);
              tabPageRef.current[tab] = 1;
              setPage(1);
            }}
          />
          <SelectField
            icon={Tag}
            label="สถานะ"
            placeholder="สถานะ"
            options={[
              { value: "", label: "สถานะ" },
              { value: "sold", label: "ขายแล้ว" },
              { value: "available", label: "พร้อมขาย" },
              { value: "unavailable", label: "ไม่พร้อมขาย" },
              { value: "draft", label: "แบบร่าง" },
            ]}
            value={sellStatus}
            onChange={(v) => {
              setSellStatus(v);
              tabPageRef.current[tab] = 1;
              setPage(1);
            }}
          />
        </div>
      );
    }

    if (tab === "sell_accessory") return null;

    if (tab === "pawn") {
      return (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <SelectField
            icon={Tag}
            label="สถานะจำนำ"
            placeholder="สถานะ"
            options={[{ value: "", label: "ทั้งหมด" }, ...pawnStatusOptions]}
            value={pawnStatus}
            onChange={(v) => {
              setPawnStatus(v);
              tabPageRef.current[tab] = 1;
              setPage(1);
            }}
          />
          <div className="hidden md:block" />
          <div className="hidden md:block" />
        </div>
      );
    }

    if (tab === "redeem") {
      return (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <SelectField
            icon={Tag}
            label="สถานะไถ่ถอน"
            placeholder="สถานะ"
            options={[{ value: "", label: "ทั้งหมด" }, ...redeemStatusOptions]}
            value={redeemStatus}
            onChange={(v) => {
              setRedeemStatus(v);
              tabPageRef.current[tab] = 1;
              setPage(1);
            }}
          />
          <div className="hidden md:block" />
          <div className="hidden md:block" />
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <SelectField
          icon={Tag}
          label="สถานะงานซ่อม"
          placeholder="สถานะ"
          options={[{ value: "", label: "ทั้งหมด" }, ...repairStatusOptions]}
          value={repairStatus}
          onChange={(v) => {
            setRepairStatus(v);
            tabPageRef.current[tab] = 1;
            setPage(1);
          }}
        />
        <div className="hidden md:block" />
        <div className="hidden md:block" />
      </div>
    );
  }, [
    tab,
    mdLoading,
    categoryOptionsForSelect,
    accessoryOptionsForSelect,
    categoryId,
    accessoryId,
    modelOptions,
    model,
    sellStatus,
    pawnStatusOptions,
    pawnStatus,
    redeemStatusOptions,
    redeemStatus,
    repairStatusOptions,
    repairStatus,
  ]);

  // ---------- tab click ----------
  function handleSwitchTab(nextTab) {
    if (nextTab === tab) return;

    setSortKey("");
    setSortDir("asc");
    setCategoryId("");
    setModel("");
    setSellStatus("");
    setAccessoryId("");
    setPawnStatus("");
    setRedeemStatus("");
    setRepairStatus("");

    tabPageRef.current[nextTab] = 1;
    setPage(1);
    setTab(nextTab);
  }

  // ---------- pagination safe ----------
  const totalPages = Math.max(1, Math.ceil((totalItems || 0) / pageSize));
  const safePage = Math.max(1, Math.min(page, totalPages));

  const handleExport = async () => {
    const [start, end] = Array.isArray(range) ? range : [null, null];
    const dateFrom = start ? toISODateOnly(start) : "";
    const dateTo = end ? toISODateOnly(end) : "";

    try {
      const params = {};

      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      if (tab === "buy_in" || tab === "sell_out") {
        if (categoryId) params.category_id = categoryId;
        if (model) params.model = model;
      }

      if (tab === "sell_accessory" && accessoryId) {
        params.accessory_id = accessoryId;
      }

      if (tab === "pawn") {
        params.pawn_status = pawnStatus || "active";
      }

      if (tab === "redeem") {
        params.redeem_status = redeemStatus || "redeemed";
      }

      if (tab === "repair" && repairStatus) {
        params.repair_status = repairStatus;
      }

      const res = await http.get("/export", {
        params,
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type: res?.headers?.["content-type"] || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      if (dateFrom && dateTo) {
        const now = new Date();
        const dd = String(now.getDate()).padStart(2, "0");
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const yyyy = String(now.getFullYear());
        a.download = `Export-${dd}-${mm}-${yyyy}.xlsx`;
      } else {
        a.download = "Export-all.xlsx";
      }
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("export error:", e);
      alert("Export ไม่สำเร็จ");
    }
  };

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safePage]);

  return (
    <div className="space-y-4 p-4 min-h-full bg-white">
      <PageHeader
        icon={Home}
        breadcrumb="Dashboard"
        title="Dashboard"
        subtitle=""
        showExport={!isAdmin}
        onExport={handleExport}
      // extraActions={
      //   <button
      //     type="button"
      //     onClick={() => navigate(`/app/pricing-queue/create`)}
      //     className="inline-flex py-3 items-center gap-2 rounded-xl bg-[#047DED] px-4 text-sm font-bold text-white shadow-xl shadow-[#047DED66] hover:bg-[#3EA2FE]"
      //   >
      //     <PlusCircle size={18} />
      //     สร้างออเดอร์
      //   </button>
      // }
      />

      {/* error */}
      {tableError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {tableError}
        </div>
      ) : null}

      {/* search + time */}
      <div className="rounded-2xl bg-[#F5F5F5] p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="w-full md:max-w-[520px]">
            <div className="flex items-center gap-3 rounded-lg border hover:border-2 border-[#1D161633] bg-white px-4 py-3">
              <Search size={18} className="text-[#1D1616B2]" />
              <input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  tabPageRef.current[tab] = 1;
                  setPage(1);
                }}
                className="w-full bg-transparent text-sm outline-none placeholder:text-[#1D161680]"
                placeholder="ค้นหารายการ..."
              />
            </div>
          </div>

          <div className="flex w-full justify-start md:w-auto md:justify-end">
            <DateRangeButton
              mode="single"
              range={range}
              minDate={minSelectableDate}
              setRange={(r) => {
                setRange(r);
                tabPageRef.current[tab] = 1;
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      {/* filters */}
      {filterFields}

      {/* tabs */}
      <div className="flex flex-1 pb-2">
        <div className="flex items-center gap-6 border-b border-slate-200 w-full overflow-x-auto no-scrollbar h-10">
          {TABS.map((t) => {
            const activeTab = tab === t.key;
            const count = tabCounts[t.key] ?? 0;

            return (
              <button
                key={t.key}
                type="button"
                onClick={() => handleSwitchTab(t.key)}
                className={classNames(
                  "relative py-2 text-sm font-medium whitespace-nowrap",
                  activeTab ? "text-[#047DED]" : "text-slate-500"
                )}
              >
                {t.label}{" "}
                <span
                  className={classNames(
                    "ml-2 inline-flex items-center rounded-lg px-2 text-[12px] font-extrabold",
                    activeTab ? "bg-[#047DED] text-white" : "bg-[#8E8B8B] text-white"
                  )}
                >
                  {count}
                </span>

                {activeTab && (
                  <span className="absolute left-0 right-0 -bottom-[1px] h-[4px] bg-[#047DED]" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* table */}
      {tab === "sell_accessory" ? (
        <div className="rounded-2xl bg-white border border-[#ECECEC]">
          <div className="overflow-x-auto">
            <div className="min-w-[1100px]">
              {/* Header */}
              <div className="grid grid-cols-[140px_360px_1fr_170px_120px] gap-4 rounded-t-xl bg-[#F7F7F7] px-6 py-4">
                <div className="text-[13px] font-semibold text-[#6B7280]">หมายเลขบิล</div>
                <div className="text-[13px] font-semibold text-[#6B7280]">รายการออเดอร์</div>
                <div className="text-[13px] font-semibold text-[#6B7280]">อุปกรณ์เสริม</div>
                <div className="text-[13px] font-semibold text-[#6B7280]">IMEI</div>
                <div className="text-[13px] font-semibold text-[#6B7280]">บิล</div>
              </div>

              {/* Body */}
              <div className="divide-y divide-transparent">
                {tableLoading ? (
                  <div className="px-6 py-10 text-center text-slate-400">
                    กำลังโหลดข้อมูล...
                  </div>
                ) : !paged.length ? (
                  <div className="px-6 py-10 text-center text-slate-400">
                    ไม่พบข้อมูล
                  </div>
                ) : (
                  paged.map((group, groupIndex) => {
                    const lines =
                      Array.isArray(group?.lines) && group.lines.length
                        ? group.lines
                        : [
                          {
                            orderItem: group?.orderItem || group?.item || "-",
                            accessory: group?.accessory || "-",
                          },
                        ];

                    const isLastGroup = groupIndex === paged.length - 1;

                    return (
                      <div
                        key={group?.id || group?.bill_no || `group_${groupIndex}`}
                        className={classNames(
                          "grid grid-cols-[140px_360px_1fr_170px_120px] gap-4 px-6",
                          isLastGroup ? "pb-5 pt-6" : "pb-8 pt-6"
                        )}
                      >
                        {/* หมายเลขบิล */}
                        <div className="text-base font-medium text-[#666666]">
                          {group?.bill_no || "-"}
                        </div>

                        {/* รายการออเดอร์ + อุปกรณ์เสริม */}
                        <div className="space-y-1">
                          {lines.map((line, index) => (
                            <div
                              key={index}
                              className={classNames(
                                "text-[15px] font-bold text-[#1F2937]",
                                index > 0 && "mt-1"
                              )}
                            >
                              {line?.orderItem || "-"}
                            </div>
                          ))}
                        </div>

                        <div className="space-y-1">
                          {lines.map((line, index) => (
                            <div
                              key={index}
                              className={classNames(
                                "text-[15px] font-medium text-[#6B7280]",
                                index > 0 && "mt-1"
                              )}
                            >
                              {line?.accessory || "-"}
                            </div>
                          ))}
                        </div>

                        {/* IMEI */}
                        <div className="text-[15px] font-semibold text-[#1F2937]">
                          {group?.imei || "-"}
                        </div>

                        {/* บิล */}
                        <div>
                          {group?.pdfUrl ? (
                            <a
                              href={group.pdfUrl}
                              download
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-2 rounded-xl bg-[#EFF6FF] px-4 py-2 text-sm font-semibold text-[#3B82F6] transition hover:bg-[#E4F0FF]"
                            >
                              <FileText size={16} />
                              บิล
                            </a>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={paged}
          rowKey={(r) => r.rowId || r.documentId || r.id || r.imei || r.ticket_no || r.job_no}
          rowHeight="h-16"
          headerBg="bg-slate-50"
          striped={false}
          tableMinWidth="min-w-[1100px]"
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={onSort}
          loading={tableLoading}
        />
      )}

      <PaginationFooter
        totalItems={totalItems}
        page={safePage}
        pageSize={pageSize}
        onPageChange={(p) => {
          tabPageRef.current[tab] = p;
          setPage(p);
        }}
        onPageSizeChange={(n) => {
          setPageSize(n);
          tabPageRef.current[tab] = 1;
          setPage(1);
        }}
        pageSizeOptions={[50, 100, 200, 500]}
      />

      <PdfModal open={openPdf} title={pdfTitle || "บิล"} url={pdfUrl} onClose={() => setOpenPdf(false)} />
    </div>
  );
}


